import type {
  CreateInferenceEndpointRequest,
  CreatePtEndpointRequest,
  ModelServingClient,
  PtServedModel,
  ServedModel,
} from "@databricks/sdk-modelserving/v1";
import type { JsonObject, ToolDef } from "@fabric-harness/sdk";

export type DatabricksServingAdminClient = Pick<
  ModelServingClient,
  | "createInferenceEndpoint"
  | "createProvisionedThroughputInferenceEndpoint"
  | "getInferenceEndpoint"
  | "putInferenceEndpointConfig"
  | "putProvisionedThroughputInferenceEndpointConfig"
  | "putInferenceEndpointAiGateway"
  | "deleteInferenceEndpoint"
>;

export interface DatabricksCustomModelEntity {
  name: string;
  entityName: string;
  entityVersion: string;
  workloadSize: "Small" | "Medium" | "Large";
  scaleToZeroEnabled?: boolean;
  environmentVars?: Record<string, string>;
}
export interface DatabricksProvisionedThroughputEntity {
  name: string;
  entityName: string;
  entityVersion: string;
  provisionedModelUnits: number;
  burstScalingEnabled?: boolean;
}
export interface DatabricksExternalModelEntity {
  name: string;
  modelName: string;
  provider: string;
  task: string;
}
export interface DatabricksAgentEntity {
  name: string;
  entityName: string;
  entityVersion?: string;
  workloadSize?: "Small" | "Medium" | "Large";
  scaleToZeroEnabled?: boolean;
}
export type DatabricksServingEndpointSpec =
  | {
      kind: "customModel";
      name: string;
      servedEntities: DatabricksCustomModelEntity[];
      tags?: Record<string, string>;
    }
  | {
      kind: "foundationModel";
      name: string;
      servedEntities: DatabricksProvisionedThroughputEntity[];
      tags?: Record<string, string>;
    }
  | {
      kind: "externalModel";
      name: string;
      servedEntities: DatabricksExternalModelEntity[];
      tags?: Record<string, string>;
    }
  | {
      kind: "agent";
      name: string;
      servedEntities: DatabricksAgentEntity[];
      tags?: Record<string, string>;
    };

export interface DatabricksAiGatewayUpdate {
  rateLimits?: Array<{
    calls?: number;
    tokens?: number;
    key?: string;
    renewalPeriod?: string;
    principal?: string;
  }>;
  guardrails?: {
    input?: { safety?: boolean; validTopics?: string[]; invalidKeywords?: string[] };
    output?: { safety?: boolean; validTopics?: string[]; invalidKeywords?: string[] };
  };
  inferenceTableConfig?: {
    catalogName?: string;
    schemaName?: string;
    tableNamePrefix?: string;
    enabled?: boolean;
  };
  usageTrackingConfig?: { enabled?: boolean };
  fallbackEnabled?: boolean;
}

export class DatabricksServingAdmin {
  constructor(private readonly client: DatabricksServingAdminClient) {}
  createEndpoint(spec: DatabricksServingEndpointSpec): Promise<unknown> {
    validate(spec);
    if (spec.kind === "foundationModel")
      return this.client.createProvisionedThroughputInferenceEndpoint(serializePt(spec));
    return this.client.createInferenceEndpoint(serialize(spec));
  }
  async getEndpoint(name: string): Promise<Record<string, unknown>> {
    return plainRecord(await this.client.getInferenceEndpoint({ name }));
  }
  updateEndpoint(
    name: string,
    servedEntities: DatabricksServingEndpointSpec["servedEntities"],
  ): Promise<unknown> {
    if (servedEntities.some((entity) => "provisionedModelUnits" in entity))
      return this.client.putProvisionedThroughputInferenceEndpointConfig({
        name,
        config: {
          servedEntities: (servedEntities as DatabricksProvisionedThroughputEntity[]).map(
            serializePtEntity,
          ),
        },
      });
    return this.client.putInferenceEndpointConfig({
      name,
      servedEntities: servedEntities.map(serializeEntity),
    });
  }
  updateAiGateway(name: string, update: DatabricksAiGatewayUpdate): Promise<unknown> {
    const rateLimits = update.rateLimits?.map((limit) => ({
      ...(limit.calls !== undefined
        ? { calls: positiveBigInt(limit.calls, "rateLimits.calls") }
        : {}),
      ...(limit.tokens !== undefined
        ? { tokens: positiveBigInt(limit.tokens, "rateLimits.tokens") }
        : {}),
      ...(limit.key !== undefined ? { key: limit.key } : {}),
      ...(limit.renewalPeriod !== undefined ? { renewalPeriod: limit.renewalPeriod } : {}),
      ...(limit.principal !== undefined ? { principal: limit.principal } : {}),
    }));
    return this.client.putInferenceEndpointAiGateway({
      name,
      ...(rateLimits ? { rateLimits } : {}),
      ...(update.guardrails ? { guardrails: update.guardrails } : {}),
      ...(update.inferenceTableConfig ? { inferenceTableConfig: update.inferenceTableConfig } : {}),
      ...(update.usageTrackingConfig ? { usageTrackingConfig: update.usageTrackingConfig } : {}),
      ...(update.fallbackEnabled !== undefined
        ? { fallbackConfig: { enabled: update.fallbackEnabled } }
        : {}),
    });
  }
  deleteEndpoint(name: string): Promise<unknown> {
    return this.client.deleteInferenceEndpoint({ name });
  }
  async waitForEndpoint(
    name: string,
    options: { timeoutMs?: number; pollIntervalMs?: number; signal?: AbortSignal } = {},
  ): Promise<Record<string, unknown>> {
    // Custom-model endpoints can remain in DEPLOYMENT_CREATING for more than 30 minutes
    // even when Databricks is still making healthy progress. Keep the wait bounded, but
    // allow the protected authoring lifecycle to survive that observed provisioning window.
    const deadline = Date.now() + (options.timeoutMs ?? 2_700_000);
    while (true) {
      if (options.signal?.aborted)
        throw options.signal.reason ?? new DOMException("Aborted", "AbortError");
      const endpoint = await this.getEndpoint(name);
      const state = (endpoint.state as Record<string, unknown> | undefined)?.ready;
      const config = (endpoint.state as Record<string, unknown> | undefined)?.configUpdate;
      if (state === "READY" && config !== "IN_PROGRESS") return endpoint;
      if (state === "NOT_READY" && config === "UPDATE_FAILED")
        throw new Error(`Databricks serving endpoint ${name} update failed.`);
      if (Date.now() >= deadline) throw new Error(`Databricks serving endpoint ${name} timed out.`);
      await sleep(jitter(options.pollIntervalMs ?? 10_000), options.signal);
    }
  }
}

export function databricksServingAdminTools(client: DatabricksServingAdminClient): ToolDef[] {
  const api = new DatabricksServingAdmin(client);
  return [
    tool(
      "databricks_create_serving_endpoint",
      "write",
      servingSchema(),
      [
        { kind: "endpoint", path: "/name" },
        {
          kind: "model",
          path: "/servedEntities/*/entityName",
          catalogQualified: true,
          optional: true,
        },
      ],
      (i) => {
        assertModelSafeServingInput(i);
        return api.createEndpoint(i as unknown as DatabricksServingEndpointSpec);
      },
    ),
    tool(
      "databricks_update_serving_endpoint",
      "write",
      updateSchema(),
      [
        { kind: "endpoint", path: "/name" },
        {
          kind: "model",
          path: "/servedEntities/*/entityName",
          catalogQualified: true,
          optional: true,
        },
      ],
      (i) => {
        assertModelSafeServingInput(i);
        return api.updateEndpoint(i.name as string, i.servedEntities as never);
      },
    ),
    tool(
      "databricks_update_ai_gateway",
      "write",
      gatewaySchema(),
      [{ kind: "endpoint", path: "/name" }],
      (i) => api.updateAiGateway(i.name as string, i as DatabricksAiGatewayUpdate),
    ),
    tool(
      "databricks_delete_serving_endpoint",
      "write",
      nameSchema(),
      [{ kind: "endpoint", path: "/name" }],
      (i) => api.deleteEndpoint(i.name as string),
    ),
  ];
}

function validate(spec: DatabricksServingEndpointSpec): void {
  if (!spec.name || !spec.servedEntities.length)
    throw new Error("databricks serving: name and servedEntities are required.");
  if (spec.kind === "foundationModel")
    for (const entity of spec.servedEntities)
      if (!Number.isSafeInteger(entity.provisionedModelUnits) || entity.provisionedModelUnits <= 0)
        throw new Error("databricks serving: provisionedModelUnits must be a positive integer.");
}
function positiveBigInt(value: number, name: string): bigint {
  if (!Number.isSafeInteger(value) || value <= 0)
    throw new Error(`databricks serving: ${name} must be a positive safe integer.`);
  return BigInt(value);
}
function serialize(
  spec: Exclude<DatabricksServingEndpointSpec, { kind: "foundationModel" }>,
): CreateInferenceEndpointRequest {
  return {
    name: spec.name,
    config: { servedEntities: spec.servedEntities.map(serializeEntity) },
    ...(spec.tags
      ? { tags: Object.entries(spec.tags).map(([key, value]) => ({ key, value })) }
      : {}),
  };
}
function serializeEntity(
  entity: DatabricksServingEndpointSpec["servedEntities"][number],
): ServedModel {
  if ("provisionedModelUnits" in entity) return serializePtEntity(entity) as never;
  if ("modelName" in entity)
    return {
      name: entity.name,
      externalModel: { name: entity.modelName, provider: entity.provider, task: entity.task },
    };
  return {
    name: entity.name,
    entityName: entity.entityName,
    ...("entityVersion" in entity && entity.entityVersion
      ? { entityVersion: entity.entityVersion }
      : {}),
    ...("workloadSize" in entity && entity.workloadSize
      ? { workloadSize: entity.workloadSize }
      : {}),
    ...("scaleToZeroEnabled" in entity && entity.scaleToZeroEnabled !== undefined
      ? { scaleToZeroEnabled: entity.scaleToZeroEnabled }
      : {}),
    ...("environmentVars" in entity && entity.environmentVars
      ? { environmentVars: entity.environmentVars }
      : {}),
  };
}

function serializePt(
  spec: Extract<DatabricksServingEndpointSpec, { kind: "foundationModel" }>,
): CreatePtEndpointRequest {
  return {
    name: spec.name,
    config: { servedEntities: spec.servedEntities.map(serializePtEntity) },
    ...(spec.tags
      ? { tags: Object.entries(spec.tags).map(([key, value]) => ({ key, value })) }
      : {}),
  };
}

function serializePtEntity(entity: DatabricksProvisionedThroughputEntity): PtServedModel {
  return {
    name: entity.name,
    entityName: entity.entityName,
    entityVersion: entity.entityVersion,
    provisionedModelUnits: BigInt(entity.provisionedModelUnits),
    ...(entity.burstScalingEnabled !== undefined
      ? { burstScalingEnabled: entity.burstScalingEnabled }
      : {}),
  };
}

function plainRecord(value: unknown): Record<string, unknown> {
  return JSON.parse(
    JSON.stringify(value, (_, item) => (typeof item === "bigint" ? Number(item) : item)),
  ) as Record<string, unknown>;
}
function tool(
  name: string,
  effect: "write",
  inputSchema: JsonObject,
  resources: JsonObject[],
  execute: (input: JsonObject) => unknown,
): ToolDef {
  return {
    name,
    description: name.replaceAll("_", " "),
    inputSchema,
    metadata: {
      effect,
      provider: "databricks",
      service: "serving",
      governance: { resources },
    },
    execute,
  };
}
function nameSchema(): JsonObject {
  return {
    type: "object",
    additionalProperties: false,
    properties: { name: { type: "string" } },
    required: ["name"],
  };
}
function entitySchema(): JsonObject {
  return {
    oneOf: [
      {
        type: "object",
        properties: {
          name: { type: "string" },
          entityName: { type: "string" },
          entityVersion: { type: "string" },
          workloadSize: { type: "string" },
          scaleToZeroEnabled: { type: "boolean" },
        },
        required: ["name", "entityName"],
      },
      {
        type: "object",
        properties: {
          name: { type: "string" },
          entityName: { type: "string" },
          entityVersion: { type: "string" },
          provisionedModelUnits: { type: "number", minimum: 1 },
          burstScalingEnabled: { type: "boolean" },
        },
        required: ["name", "entityName", "entityVersion", "provisionedModelUnits"],
      },
      {
        type: "object",
        properties: {
          name: { type: "string" },
          modelName: { type: "string" },
          provider: { type: "string" },
          task: { type: "string" },
        },
        required: ["name", "modelName", "provider", "task"],
      },
    ],
  };
}
function assertModelSafeServingInput(input: JsonObject): void {
  const entities = Array.isArray(input.servedEntities) ? input.servedEntities : [];
  if (
    entities.some(
      (entity) =>
        entity &&
        typeof entity === "object" &&
        !Array.isArray(entity) &&
        "environmentVars" in entity,
    )
  ) {
    throw new Error(
      "databricks serving: raw environmentVars are available only on the typed client, not model-facing ToolDefs.",
    );
  }
}
function servingSchema(): JsonObject {
  return {
    type: "object",
    additionalProperties: false,
    properties: {
      kind: { type: "string", enum: ["customModel", "foundationModel", "externalModel", "agent"] },
      name: { type: "string" },
      servedEntities: { type: "array", items: entitySchema() },
      tags: { type: "object", additionalProperties: { type: "string" } },
    },
    required: ["kind", "name", "servedEntities"],
  };
}
function updateSchema(): JsonObject {
  return {
    type: "object",
    additionalProperties: false,
    properties: {
      name: { type: "string" },
      servedEntities: { type: "array", items: entitySchema() },
    },
    required: ["name", "servedEntities"],
  };
}
function gatewaySchema(): JsonObject {
  return {
    type: "object",
    additionalProperties: false,
    properties: {
      name: { type: "string" },
      rateLimits: {
        type: "array",
        items: {
          type: "object",
          additionalProperties: false,
          properties: {
            calls: { type: "integer", minimum: 1 },
            tokens: { type: "integer", minimum: 1 },
            key: { type: "string" },
            renewalPeriod: { type: "string" },
            principal: { type: "string" },
          },
        },
      },
      guardrails: { type: "object", additionalProperties: true },
      inferenceTableConfig: { type: "object", additionalProperties: true },
      usageTrackingConfig: { type: "object", additionalProperties: true },
      fallbackEnabled: { type: "boolean" },
    },
    required: ["name"],
  };
}
function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
      },
      { once: true },
    );
  });
}
function jitter(ms: number): number {
  return Math.max(1, Math.round(ms * (0.9 + Math.random() * 0.2)));
}
